<script setup lang="ts">
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'
import { formatDateOnly } from '@/utils/formatters'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { useImportStore } from '../stores/importStore'
import type { IImportHistory } from '../types/IImportHistory'

const props = defineProps<{
  modelValue: boolean
  importType: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'resume': [uuid: string]
}>()

const importStore = useImportStore()
const permissionsStore = usePermissionsStore()

// Раскрытые записи истории со списком ошибок
const expandedErrorIds = ref<Set<number>>(new Set())

const hasErrors = (item: IImportHistory) => Array.isArray(item.errors) && item.errors.length > 0

const isErrorsExpanded = (id: number) => expandedErrorIds.value.has(id)

function toggleErrors(id: number) {
  const next = new Set(expandedErrorIds.value)
  if (next.has(id))
    next.delete(id)
  else
    next.add(id)
  expandedErrorIds.value = next
}

const page = ref(1)
const pageSize = ref(25)
const historyLoading = ref(false)

const dataTablePaginationKey = Symbol.for('vuetify:data-table-pagination')

async function cancelDraft(uuid: string) {
  await importStore.cancelImportByUuid(props.importType, uuid)
  await load()
}

async function cancelTask(id: number) {
  await importStore.cancelImportById(props.importType, id)
  await load()
}

async function downloadFile(id: number, fileName?: string) {
  await importStore.downloadImportHistoryFile(id, props.importType, fileName)
}

function cursorFrom(link: string | null) {
  if (!link)
    return ''
  const matched = link.match(/[?&]cursor=([^&]+)/)
  return matched ? decodeURIComponent(matched[1]) : ''
}

const pageCount = computed(() => {
  if (!importStore.historyCount)
    return 1
  return Math.max(1, Math.ceil(importStore.historyCount / pageSize.value))
})

const itemsLength = computed(() => importStore.historyCount)
const startIndex = computed(() => itemsLength.value ? pageSize.value * (page.value - 1) : 0)
const stopIndex = computed(() =>
  itemsLength.value ? Math.min(itemsLength.value, startIndex.value + pageSize.value) : 0,
)

async function load(cursor = '') {
  historyLoading.value = true
  try {
    await importStore.fetchImportHistory({
      importType: props.importType,
      cursor: cursor || undefined,
      pageSize: pageSize.value,
    })
  } finally {
    historyLoading.value = false
  }
}

async function setPage(value: number) {
  const next = Math.min(Math.max(value, 1), pageCount.value)
  if (next === page.value)
    return
  if (next === page.value + 1) {
    const cursor = cursorFrom(importStore.historyNext)
    if (!cursor)
      return
    page.value = next
    await load(cursor)
    return
  }
  if (next === page.value - 1) {
    const cursor = cursorFrom(importStore.historyPrevious)
    page.value = next
    await load(cursor)
  }
}

const pageModel = computed({
  get: () => page.value,
  set: (value: number) => {
    setPage(value)
  },
})

const pageSizeModel = computed({
  get: () => pageSize.value,
  set: (value: number) => {
    if (![25, 50, 100].includes(value) || value === pageSize.value)
      return
    pageSize.value = value
    page.value = 1
    load()
  },
})

provide(dataTablePaginationKey, {
  page: pageModel,
  itemsPerPage: pageSizeModel,
  startIndex,
  stopIndex,
  pageCount,
  itemsLength,
  nextPage: () => setPage(page.value + 1),
  prevPage: () => setPage(page.value - 1),
  setPage,
  setItemsPerPage: (value: number) => {
    pageSizeModel.value = value
  },
})

const hideNextButton = computed(() => !importStore.historyNext || page.value >= pageCount.value)

function close() {
  emit('update:modelValue', false)
}

watch(() => props.modelValue, isOpen => {
  if (!isOpen)
    return
  page.value = 1
  load()
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    fullscreen
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard class="h-100 d-flex flex-column overflow-hidden">
      <div class="d-flex align-center justify-space-between pa-6 flex-shrink-0">
        <span class="text-h4">История импорта</span>
        <CustomCloseButton :close-action="close" />
      </div>
      <div class="px-6 pb-6 d-flex flex-column flex-grow-1 overflow-hidden min-h-0">
        <div v-if="historyLoading" class="d-flex justify-center align-center flex-grow-1 py-8">
          <AnimatedLogoLoader />
        </div>
        <div v-else-if="importStore.history.length" class="overflow-auto flex-grow-1 min-h-0">
          <table class="import-plain-table">
            <thead>
              <tr>
                <th>Файл</th>
                <th>Статус</th>
                <th>Загружено</th>
                <th>Пропущено</th>
                <th>Ошибки</th>
                <th>Кто импортировал</th>
                <th>Дата</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              <template
                v-for="item in importStore.history"
                :key="item.id"
              >
              <tr>
                <td>{{ item.file_name }}</td>
                <td>{{ item.status_display }}</td>
                <td>{{ item.imported_items }}</td>
                <td>{{ item.skipped_items ?? 0 }}</td>
                <td>
                  <!-- Раскрытие списка ошибок (треугольник как у подзадач в /tasks), только если бэк их прислал -->
                  <div
                    v-if="hasErrors(item)"
                    class="d-inline-flex align-center cursor-pointer text-primary font-weight-medium"
                    @click="toggleErrors(item.id)"
                  >
                    <VIcon
                      :icon="isErrorsExpanded(item.id) ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'"
                      size="18"
                      class="mr-1"
                    />
                    <span>{{ item.failed_items }}</span>
                  </div>
                  <template v-else>
                    {{ item.failed_items }}
                  </template>
                </td>
                <td>{{ item.employee_name }}</td>
                <td>{{ formatDateOnly(item.created_at) }}</td>
                <td class="text-no-wrap">
                  <VBtn
                    v-if="item.can_resume && item.status !== 'cancelled' && item.status !== 'completed' && item.status !== 'completed_with_errors'"
                    size="small"
                    variant="tonal"
                    color="primary"
                    class="me-2"
                    @click="emit('resume', item.uuid || String(item.id))"
                  >
                    Продолжить
                  </VBtn>
                  <VBtn
                    v-if="item.can_cancel"
                    size="small"
                    variant="outlined"
                    color="error"
                    class="me-2"
                    @click="item.uuid ? cancelDraft(item.uuid) : cancelTask(item.id)"
                  >
                    Отменить
                  </VBtn>
                  <VBtn
                    v-if="item.id && item.file_name && item.status !== 'cancelled' && (item.is_own || permissionsStore.isSuperuser)"
                    size="small"
                    variant="tonal"
                    color="secondary"
                    prepend-icon="ri-download-2-line"
                    @click="downloadFile(item.id, item.file_name)"
                  >
                    Скачать
                  </VBtn>
                </td>
              </tr>
              <tr v-if="hasErrors(item) && isErrorsExpanded(item.id)" class="import-errors-row">
                <td colspan="8">
                  <div class="import-errors-list d-flex flex-column ga-1">
                    <span
                      v-for="(err, idx) in item.errors"
                      :key="idx"
                      class="text-body-2"
                    >
                      <span v-if="err.row != null" class="font-weight-medium">Строка {{ err.row }}:</span>
                      {{ err.error }}
                    </span>
                  </div>
                </td>
              </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div
          v-else
          class="pa-4 text-medium-emphasis flex-grow-1"
        >
          Нет записей
        </div>
        <div
          v-if="importStore.historyCount"
          class="custom-data-table-footer flex-shrink-0 pt-4"
          :class="{ 'hide-next-button': hideNextButton }"
        >
          <VDataTableFooter
            show-current-page
            :items-per-page-options="[25, 50, 100]"
          />
        </div>
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled='true'] {
      display: none !important;
    }
  }

  &.hide-next-button {
    :deep(.v-pagination__next) {
      display: none !important;
    }
  }
}


// Раскрытая строка ошибок — фон как у строк подзадач в /tasks, длинный список скроллится
.import-errors-row td {
  background-color: rgba(var(--v-theme-on-surface), 0.02);
}

.import-errors-list {
  max-block-size: 240px;
  overflow-y: auto;
}

.import-plain-table {
  border-collapse: collapse;
  width: 100%;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: rgb(var(--v-theme-surface));
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    font-size: 15px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 22px;
    padding: 0.75rem 1rem;
    text-align: start;
    white-space: nowrap;
    border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  td {
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    padding: 0.75rem 1rem;
    text-align: start;
  }

  th:not(:last-child)::after {
    background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
    content: '';
    height: 14px;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
  }
}
</style>
